import { setHeaderMenuStyle } from '@theme/utilities';

class MegaMenu extends HTMLElement {
  constructor() {
    super();
    this.mouseEnterHandlers = new Map();
    this.mouseLeaveHandlers = new Map();
    /** @type {Map<Element, { toggleMenu: (event: Event) => void; outsideClickHandler: (event: MouseEvent) => void; keydownHandler: (event: KeyboardEvent) => void }>} */
    this.clickHandlers = new Map();
    this.megaMenuHoverHandler = null;

    this.menuObserver = null;
    this.menuHeightObserver = null;

    // Recalculation of the mega-menu height is triggered only by header-scoped
    // signals (see handleMegaMenuHeight): a ResizeObserver on the header groups
    // and a childList MutationObserver on #header-group — never document.body.
    this.headerGroupResizeObserver = null;
    this._heightRafId = null;
    this._megaMenuHeightResizeHandler = null;
  }

  static getMenuData() {
    // @ts-ignore
    return window.menu_json;
  }

  static get prefix() {
    const theme = window.Theme || (typeof Theme !== 'undefined' ? Theme : undefined);
    return theme?.routes?.root_url_prefix || '/';
  }

  static getUrl(url) {
    const theme = window.Theme || (typeof Theme !== 'undefined' ? Theme : undefined);
    if (typeof theme?.getLocalizedUrl === 'function') {
      return theme.getLocalizedUrl(url);
    }

    const origin = theme?.routes?.request_origin;
    return (url || '').replace(`${origin}/`, this.prefix);
  }

  connectedCallback() {
    // Prevent multiple initialization across all fallbacks
    if (this._megaMenuInitialized) return;

    const initOnce = () => {
      if (this._megaMenuInitialized) return;
      this._megaMenuInitialized = true;
      this.initMegaMenu();
    };

    try {
      const observer = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1]; // Get the latest LCP entry
        if (lastEntry) {
          // Check if not already injected
          initOnce();
          observer.disconnect(); // Disconnect observer after LCP is detected
        }
      });

      observer.observe({ type: 'largest-contentful-paint', buffered: true });

      // Fallback for browsers where PerformanceObserver might not trigger
      setTimeout(() => {
        if (!this._megaMenuInitialized) {
          initOnce();
        }
      }, 2000); // Adjust the timeout duration as needed
    } catch (error) {
      // Fallback in case PerformanceObserver fails
      initOnce();
    }

    window.addEventListener('resize', setHeaderMenuStyle);
  }

  disconnectedCallback() {
    if (this.menuObserver) {
      this.menuObserver.disconnect();
      this.menuObserver = null;
    }
    if (this.menuHeightObserver) {
      this.menuHeightObserver.disconnect();
      this.menuHeightObserver = null;
    }
    if (this.headerGroupResizeObserver) {
      this.headerGroupResizeObserver.disconnect();
      this.headerGroupResizeObserver = null;
    }
    if (this._heightRafId != null) {
      cancelAnimationFrame(this._heightRafId);
      this._heightRafId = null;
    }
    if (this._megaMenuHeightResizeHandler) {
      window.removeEventListener('resize', this._megaMenuHeightResizeHandler);
      this._megaMenuHeightResizeHandler = null;
    }
    // Cleanup event listeners and timeouts when the element is removed from the DOM
    this.cleanupMegaMenuHover();

    window.removeEventListener('resize', setHeaderMenuStyle);
  }

  initMegaMenu() {
    this.cleanupMegaMenuHover(); // IMPORTANT

    this.injectContent();
    this.handleMegaMenuHeight();
    this.handleMegaMenuHover();
  }

  // @ts-ignore
  trackNavEvent(level, link, linkText) {
    // @ts-ignore
    if (!window.dataLayer) window.dataLayer = [];

    link.addEventListener('click', () => {
      // @ts-ignore
      window.dataLayer.push({
        event: 'select_navitem_dev',
        nav_level: level,
        nav_type: 'main-navigation',
        category_name: linkText,
        category_id: linkText,
      });
    });
  }

  /**
   * @param {Element | null | undefined} megaMenu
   * @returns {boolean}
   */
  isButtonTriggerMegaMenu(megaMenu) {
    return megaMenu?.classList.contains('mega-menu__component--button-trigger') ?? false;
  }

  /**
   * @param {Element | null | undefined} megaMenuComponent
   * @returns {string}
   */
  getViewAllText(megaMenuComponent) {
    const viewAllFromComponent =
      megaMenuComponent instanceof HTMLElement ? megaMenuComponent.dataset.viewAll : undefined;

    return viewAllFromComponent || this.getAttribute('view-all') || 'Tout afficher';
  }

  /**
   * @param {Element} megaMenu
   */
  resetSubmenuState(megaMenu) {
    megaMenu.classList.remove('mega-menu__component--submenu-active');
    megaMenu.querySelectorAll('.mega-menu__item').forEach((item) => {
      item.classList.remove('mega-menu__item--active');
    });
    megaMenu.querySelectorAll('.mega-menu__submenus').forEach((submenu) => {
      submenu.classList.remove('mega-menu__submenus--active');
      submenu.setAttribute('aria-hidden', 'true');
    });
  }

  injectContent() {
    const menuData = MegaMenu.getMenuData();
    if (!menuData || !menuData.menuItems) return;

    const menuItemsContainer = this.querySelector('.mega-menu__items');
    const submenusContainer = this.querySelector('.mega-menu__submenus-container');

    if (!menuItemsContainer || !submenusContainer) {
      console.error('Menu containers not found in the DOM.');
      return;
    }

    const megaMenuComponent = menuItemsContainer.closest('.mega-menu__component');
    const isButtonTrigger = this.isButtonTriggerMegaMenu(megaMenuComponent);
    const viewAllText = this.getViewAllText(megaMenuComponent);

    // @ts-ignore
    menuData.menuItems.forEach((menuItem, index) => {
      const menuItemElement = document.createElement('li');
      menuItemElement.className = `mega-menu__item ${!isButtonTrigger && index === 0 ? 'mega-menu__item--active' : ''}`;
      menuItemElement.setAttribute('menu-data', `menu-${index + 1}`);

      menuItemElement.innerHTML = `
        <a
          href="${MegaMenu.getUrl(menuItem.url)}"
          class="mega-menu__item-link"
        >
          <div class="image mega-menu__item-img" style="--object-fit: contain">
            <img src="${menuItem.image}" class="image__img image--contain" loading="lazy" alt="${menuItem.title}" />
          </div>

          <div class="text" style="--text-color: var(--color--secondary);">
            ${menuItem.title}
          </div>

          <div class="mega-menu__item-svg">
            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
              <path d="M1 1.65945L6.08884 6.74829C6.12191 6.78131 6.14814 6.82053 6.16604 6.8637C6.18394 6.90687 6.19316 6.95315 6.19316 6.99988C6.19316 7.04662 6.18394 7.09289 6.16604 7.13606C6.14814 7.17923 6.12191 7.21845 6.08884 7.25147L1 12.3403"
                stroke="#002341" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </a>
      `;

      menuItemsContainer.appendChild(menuItemElement);

      if (!menuItem.subMenu || menuItem.subMenu.length === 0) {
        return;
      }

      // Creating submenu for each menu item
      const submenuElement = document.createElement('div');
      submenuElement.className = `mega-menu__submenus ${!isButtonTrigger && index === 0 ? 'mega-menu__submenus--active' : ''}`;
      submenuElement.setAttribute(
        'aria-hidden',
        !isButtonTrigger && index === 0 ? 'false' : 'true'
      );
      submenuElement.setAttribute('menu-data', `menu-${index + 1}`);
      submenuElement.setAttribute('id', `mega-menu-submenu-${index + 1}`);
      submenuElement.setAttribute('aria-labelledby', `mega-menu-item-${index + 1}`);
      submenuElement.setAttribute('data-index', index);

      const submenuWrapper = document.createElement('div');
      submenuWrapper.className = 'mega-menu__submenus-wrapper';

      // @ts-ignore
      menuItem.subMenu.forEach((subItem) => {
        const subCategory = document.createElement('div');
        if (!subCategory) return;

        subCategory.className = 'mega-menu__submenu';

        subCategory.innerHTML = `
            <div class="image mega-menu__submenu-img" style="--object-fit: contain">
              ${subItem.image ? `<img src="${subItem.image}" class="image__img image--contain" loading="lazy" alt="${subItem.title}" />` : ''}
            </div>
            <ul class="mega-menu__submenu-container">
              <li class="mega-menu__submenu-item">
                <a href="${MegaMenu.getUrl(subItem.url)}" class="mega-menu__submenu-title mega-menu__item-breaker">
                  ${subItem.title}
                </a>
              </li>
            </ul>
          `;

        // Third level items
        // @ts-ignore
        subItem.subMenu?.forEach((thirdLevelItem, thirdIndex) => {
          const el = subCategory.querySelector('.mega-menu__submenu-container');
          if (!el) return;

          if (thirdIndex === 5) {
            el.innerHTML += `
                <li class="mega-menu__submenu-item">
                  <a href="${MegaMenu.getUrl(subItem.url)}" class="mega-menu__submenu-more">
                    ${viewAllText}
                  </a>
                </li>
              `;
            return;
          }

          if (thirdIndex > 5) return;

          el.innerHTML += `
              <li class="mega-menu__submenu-item">
                <a href="${MegaMenu.getUrl(thirdLevelItem.url)}" class="mega-menu__item-breaker">
                  ${thirdLevelItem.title}
                </a>
              </li>
            `;
        });

        submenuWrapper.appendChild(subCategory);
      });

      submenuElement.appendChild(submenuWrapper);
      submenusContainer.appendChild(submenuElement);
    });

    // Level 2
    document.querySelectorAll('.mega-menu__item').forEach((item) => {
      const link = item.querySelector('a');
      if (link) {
        this.trackNavEvent('2', item, link.textContent?.trim());
      }
    });

    // Level 3
    document
      .querySelectorAll('.mega-menu__submenu-title, .mega-menu__submenu-item a')
      .forEach((link) => {
        this.trackNavEvent('3', link, link.textContent?.trim());
      });
  }

  // Get mega menu height
  //
  // The mega-menu height only depends on the header group heights, so the
  // recalculation is scoped to header-only signals: initialisation, a
  // rAF-coalesced window resize, a ResizeObserver on the header groups, and a
  // childList MutationObserver on #header-group (for sections being added or
  // removed). It never observes document.body, so unrelated page mutations
  // (e.g. Algolia autocomplete DOM churn) can no longer trigger it.
  handleMegaMenuHeight() {
    const headerGroupRoot = document.querySelector('#header-group');

    // Cached query result. Only re-queried when the header's node set actually
    // changes (inside the scoped MutationObserver callback below), never on
    // every resize/ResizeObserver tick.
    /** @type {Element[]} */
    let cachedHeaderGroups = [];

    const refreshHeaderGroups = () => {
      cachedHeaderGroups = Array.from(
        (headerGroupRoot ?? document).querySelectorAll('.shopify-section-group-header-group')
      );
      return cachedHeaderGroups;
    };

    // rAF-coalesced recalculation. Reads all geometry first (batched), then
    // performs a single write to a CSS custom property on this element — a
    // non-layout-participating target that is never one of the observed
    // elements, which prevents a ResizeObserver feedback loop.
    const recalcMegaMenuHeight = () => {
      if (this._heightRafId != null) return; // already scheduled for this frame
      this._heightRafId = window.requestAnimationFrame(() => {
        this._heightRafId = null;
        if (cachedHeaderGroups.length === 0) return;

        // READ phase — batch all geometry reads before any write.
        let height = 0;
        for (const hg of cachedHeaderGroups) {
          height += hg.getBoundingClientRect().height; // accounts for borders/padding
        }

        // WRITE phase.
        // dvh tracks the visible viewport on tablets, where 100vh includes the browser toolbar.
        this.style.setProperty('--mega-menu-height', `calc(100dvh - ${height}px)`);
      });
    };

    // ResizeObserver is the right tool for the "something changed size" case:
    // attach it to each header group so any header height change recalculates,
    // without observing document.body.
    this.headerGroupResizeObserver = new ResizeObserver(() => recalcMegaMenuHeight());

    const observeHeaderGroupSizes = () => {
      if (!this.headerGroupResizeObserver) return;
      this.headerGroupResizeObserver.disconnect();
      for (const hg of cachedHeaderGroups) {
        this.headerGroupResizeObserver.observe(hg);
      }
    };

    // Initialise once the header groups exist (bounded retry).
    let initAttempts = 0;
    const initHeight = () => {
      if (refreshHeaderGroups().length === 0) {
        if (initAttempts++ < 50) {
          setTimeout(initHeight, 100); // Retry every 100ms
        }
        return;
      }
      observeHeaderGroupSizes();
      recalcMegaMenuHeight();
    };

    // Window resize: rAF-coalesced, never a synchronous recalc per tick.
    this._megaMenuHeightResizeHandler = recalcMegaMenuHeight;
    window.addEventListener('resize', this._megaMenuHeightResizeHandler);

    // Scoped MutationObserver: only watches #header-group for header sections
    // being added/removed (childList, not a body-wide subtree). This is the
    // only place the querySelectorAll is re-run.
    if (headerGroupRoot) {
      this.menuHeightObserver = new MutationObserver(() => {
        refreshHeaderGroups();
        observeHeaderGroupSizes();
        recalcMegaMenuHeight();
      });
      this.menuHeightObserver.observe(headerGroupRoot, { childList: true });
    }

    // Call the height function immediately if the DOM is already loaded.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initHeight, { once: true });
    } else {
      initHeight();
    }
  }

  // Handle mega menu hover with MutationObserver
  handleMegaMenuHover() {
    const megaMenuItems = this.querySelectorAll('.menu-list__list-item--mega-menu');

    // MutationObserver to detect added menu items
    this.menuObserver = new MutationObserver(() => {
      const updatedMenuItems = this.querySelectorAll('.menu-list__list-item--mega-menu');
      updatedMenuItems.forEach((item) => {
        if (!item.hasAttribute('data-listener-attached')) {
          this.attachHoverListeners(item);
          item.setAttribute('data-listener-attached', 'true');
        }
      });
    });

    // Start observing for changes in the mega menu container
    this.menuObserver.observe(this, { childList: true, subtree: true });

    // Initially attach listeners for already existing items
    megaMenuItems.forEach((item) => {
      if (!item.hasAttribute('data-listener-attached')) {
        this.attachHoverListeners(item);
        item.setAttribute('data-listener-attached', 'true');
      }
    });
  }

  /**
   * @param {Element} megaMenu
   * @param {Element} megaMenuLink
   */
  openMegaMenu(megaMenu, megaMenuLink) {
    megaMenu.classList.add('mega-menu__component--active');
    megaMenuLink.setAttribute('aria-expanded', 'true');
    document.querySelector('body')?.classList.add('overflow-hidden');

    if (this.isButtonTriggerMegaMenu(megaMenu)) {
      this.resetSubmenuState(megaMenu);
    }
  }

  /**
   * @param {Element} megaMenu
   * @param {Element} megaMenuLink
   */
  closeMegaMenu(megaMenu, megaMenuLink) {
    megaMenu.classList.remove('mega-menu__component--active');
    megaMenuLink.setAttribute('aria-expanded', 'false');
    document.querySelector('body')?.classList.remove('overflow-hidden');

    if (this.isButtonTriggerMegaMenu(megaMenu)) {
      this.resetSubmenuState(megaMenu);
    }
  }

  /**
   * @param {Element} item
   * @param {Element} megaMenu
   * @param {Element} megaMenuLink
   */
  attachClickListeners(item, megaMenu, megaMenuLink) {
    const openMenu = () => this.openMegaMenu(megaMenu, megaMenuLink);
    const closeMenu = () => this.closeMegaMenu(megaMenu, megaMenuLink);

    /** @param {Event} event */
    const toggleMenu = (event) => {
      event.preventDefault();
      event.stopPropagation();

      if (megaMenu.classList.contains('mega-menu__component--active')) {
        closeMenu();
      } else {
        openMenu();
      }
    };

    /** @param {MouseEvent} event */
    const outsideClickHandler = (event) => {
      const target = event.target;

      if (
        megaMenu.classList.contains('mega-menu__component--active') &&
        target instanceof Node &&
        !item.contains(target)
      ) {
        closeMenu();
      }
    };

    /** @param {KeyboardEvent} event */
    const keydownHandler = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        toggleMenu(event);

        if (!megaMenu.classList.contains('mega-menu__component--active')) return;

        setTimeout(() => {
          const firstSubmenuItem = megaMenu.querySelector('.mega-menu__item a');
          if (firstSubmenuItem instanceof HTMLElement) {
            firstSubmenuItem.focus();
          }
        }, 250);
      }

      if (event.key === 'Escape' && megaMenu.classList.contains('mega-menu__component--active')) {
        event.preventDefault();
        closeMenu();
      }
    };

    megaMenuLink.addEventListener('click', toggleMenu);
    document.addEventListener('click', outsideClickHandler);
    megaMenuLink.addEventListener('keydown', keydownHandler);

    this.clickHandlers.set(item, { toggleMenu, outsideClickHandler, keydownHandler });
    this.handleMegaMenuItemHover(megaMenu, megaMenuLink);
  }

  // Attach hover listeners to a single mega menu item
  // @ts-ignore
  attachHoverListeners(item) {
    const megaMenu = item.querySelector('.mega-menu__component');
    const megaMenuLink = item.querySelector('.menu-list__link');

    if (!megaMenu || !megaMenuLink) {
      console.error('Mega menu component or link not found for item:', item);
      return;
    }

    if (item.classList.contains('menu-list__list-item--button-trigger')) {
      this.attachClickListeners(item, megaMenu, megaMenuLink);
      return;
    }

    // @ts-ignore
    let leaveTimeout;
    // @ts-ignore
    let enterTimeout;

    // Create the mouseEnter and mouseLeave handlers
    // @ts-ignore
    const openMenu = (e) => {
      if (
        !e.target.matches(':hover') && // Check if still hovering
        e.type !== 'keydown'
      )
        return;

      // @ts-ignore
      clearTimeout(leaveTimeout); // Clear any pending leave timeout
      enterTimeout = setTimeout(() => {
        this.openMegaMenu(megaMenu, megaMenuLink);
      }, 200);
    };

    const closeMenu = () => {
      // @ts-ignore
      clearTimeout(enterTimeout); // Clear any pending enter timeout
      leaveTimeout = setTimeout(() => {
        this.closeMegaMenu(megaMenu, megaMenuLink);
      }, 200);
    };

    // Store the handlers for later removal
    this.mouseEnterHandlers.set(item, openMenu);
    this.mouseLeaveHandlers.set(item, closeMenu);

    // Attach the event listeners
    item.addEventListener('mouseenter', openMenu);
    item.addEventListener('mouseleave', closeMenu);

    // Keyboard / focus
    // @ts-ignore
    megaMenuLink.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();

        openMenu(e);

        // Wait until menu is visible
        setTimeout(() => {
          const megaMenuComponent = item.querySelector('.mega-menu__component');

          if (!megaMenuComponent) return;

          // Find first submenu in the opened mega menu
          const firstSubmenuItem = megaMenuComponent.querySelector('.mega-menu__item a');
          if (firstSubmenuItem) {
            firstSubmenuItem.focus();
          }
        }, 250); // wait for open animation / DOM update
      }
    });

    this.handleMegaMenuItemHover(megaMenu, megaMenuLink);
  }

  // Handle mega menu item hover
  // @ts-ignore
  handleMegaMenuItemHover(megaMenu, megaMenuLink) {
    const megaMenuItems = megaMenu.querySelectorAll('.mega-menu__item');
    const submenus = megaMenu.querySelectorAll('.mega-menu__submenus');

    // @ts-ignore
    megaMenuItems.forEach((megaMenuItem, itemIndex) => {
      // @ts-ignore
      const openSubmenu = (event, shouldFocusFirstLink = false) => {
        if (this.megaMenuHoverHandler) {
          clearTimeout(this.megaMenuHoverHandler);
        }

        this.megaMenuHoverHandler = setTimeout(() => {
          if (
            !event.target.matches(':hover') && // Check if still hovering
            event.type !== 'focusin' &&
            event.type !== 'keydown'
          )
            return;

          // Deactivate all items
          // @ts-ignore
          megaMenuItems.forEach((item) => item.classList.remove('mega-menu__item--active'));
          megaMenuItem.classList.add('mega-menu__item--active');
          // Render the current submenu from the menu data
          // @ts-ignore
          submenus.forEach((submenu) => {
            submenu.classList.remove('mega-menu__submenus--active');
            submenu.setAttribute('aria-hidden', 'true');
          });

          const submenuEl = megaMenu.querySelector(
            `.mega-menu__submenus[data-index="${itemIndex}"]`
          );

          if (!submenuEl) return;

          const menuData = MegaMenu.getMenuData();
          const currentSubmenuData = menuData.menuItems[itemIndex];

          if (currentSubmenuData && currentSubmenuData.subMenu) {
            submenuEl.classList.add('mega-menu__submenus--active');
            submenuEl.setAttribute('aria-hidden', 'false');

            if (this.isButtonTriggerMegaMenu(megaMenu)) {
              megaMenu.classList.add('mega-menu__component--submenu-active');
            }
          } else if (this.isButtonTriggerMegaMenu(megaMenu)) {
            megaMenu.classList.remove('mega-menu__component--submenu-active');
          }

          // Focus handling
          const submenuFocusable = submenuEl.querySelectorAll('a');
          const firstFocusable = submenuFocusable[0];
          const lastFocusable = submenuFocusable[submenuFocusable.length - 1];

          if (shouldFocusFirstLink && firstFocusable) {
            firstFocusable.focus();
          }

          // Attach keyboard behavior ONLY once
          if (!submenuEl.dataset.keyboardAttached) {
            submenuEl.dataset.keyboardAttached = 'true';

            // Helper function to return focus to the main menu trigger
            const backToMenu = () => {
              const trigger = megaMenuItem.querySelector('a');
              if (trigger) {
                trigger.focus();
              }
            };

            // Tab + Shift trap (first → back to trigger)
            if (firstFocusable) {
              // @ts-ignore
              firstFocusable.addEventListener('keydown', (e) => {
                if (e.key === 'Tab' && e.shiftKey) {
                  e.preventDefault();
                  backToMenu();
                }
              });
            }

            // Tab trap (last → back to trigger)
            if (lastFocusable) {
              // @ts-ignore
              lastFocusable.addEventListener('keydown', (e) => {
                if (e.key === 'Tab' && !e.shiftKey) {
                  e.preventDefault();
                  backToMenu();
                }
              });
            }

            // Escape handler (close submenu + return focus)
            // @ts-ignore
            submenuEl.addEventListener('keydown', (e) => {
              if (e.key === 'Escape') {
                e.preventDefault();

                submenuEl.classList.remove('mega-menu__submenus--active');
                submenuEl.setAttribute('aria-hidden', 'true');

                megaMenuItem.classList.remove('mega-menu__item--active');

                if (this.isButtonTriggerMegaMenu(megaMenu)) {
                  megaMenu.classList.remove('mega-menu__component--submenu-active');
                }

                const trigger = megaMenuItem.querySelector('a');
                if (trigger) {
                  trigger.focus();
                }
              }
            });
          }
        }, 50);
      };

      // @ts-ignore
      megaMenuItem.addEventListener('mouseenter', (e) => openSubmenu(e));
      // @ts-ignore
      megaMenuItem.addEventListener('focusin', (e) => openSubmenu(e));

      const triggerLink = megaMenuItem.querySelector('a');
      if (triggerLink) {
        // @ts-ignore
        triggerLink.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            openSubmenu(e, true);
          }

          if (e.key === 'Escape') {
            e.preventDefault();

            // Close the submenu
            const submenuEl = megaMenu.querySelector(
              `.mega-menu__submenus[data-index="${itemIndex}"]`
            );

            if (submenuEl) {
              submenuEl.classList.remove('mega-menu__submenus--active');
              submenuEl.setAttribute('aria-hidden', 'true');
            }

            megaMenuItem.classList.remove('mega-menu__item--active');

            if (this.isButtonTriggerMegaMenu(megaMenu)) {
              megaMenu.classList.remove('mega-menu__component--submenu-active');
            }

            // Focus back to main header trigger
            const mainTrigger = megaMenu.previousElementSibling;
            if (mainTrigger) {
              mainTrigger.focus();
            }

            this.closeMegaMenu(megaMenu, megaMenuLink);
          }
        });
      }
    });
  }

  // Cleanup hover event listeners
  cleanupMegaMenuHover() {
    const megaMenuItems = this.querySelectorAll('.menu-list__list-item--mega-menu');
    megaMenuItems.forEach((item) => {
      item.removeAttribute('data-listener-attached'); // Allow re-attachment of listeners when needed

      const megaMenu = item.querySelector('.mega-menu__component');
      const megaMenuLink = item.querySelector('.menu-list__link');
      const clickHandler = this.clickHandlers.get(item);

      if (clickHandler && megaMenuLink) {
        megaMenuLink.removeEventListener('click', clickHandler.toggleMenu);
        megaMenuLink.removeEventListener('keydown', clickHandler.keydownHandler);
        document.removeEventListener('click', clickHandler.outsideClickHandler);
      }

      if (megaMenu) {
        // Remove the event listeners using the stored handlers
        item.removeEventListener('mouseenter', this.mouseEnterHandlers.get(item));
        item.removeEventListener('mouseleave', this.mouseLeaveHandlers.get(item));
      }
    });

    // Clear the handlers after they are removed
    this.mouseEnterHandlers.clear();
    this.mouseLeaveHandlers.clear();
    this.clickHandlers.clear();
  }
}
if (!customElements.get('mega-menu')) {
  customElements.define('mega-menu', MegaMenu);
}
